import React, { memo, useRef, useState } from 'react'

import { HiMiniMinus, HiMiniPlus, HiMiniXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Badge from '@/components/common/Badge'
import IconButton from '@/components/common/IconButtonV2'

import { useMounted } from '@/hooks/common/useMounted'

interface FilterContainer {
  title: string
  count: number | string
  onClear: () => void
  children: React.ReactNode
  className?: string
}

const FilterContainer: React.FC<FilterContainer> = ({
  title,
  count,
  onClear,
  children,
  className,
}) => {
  const [expanded, setExpanded] = useState<boolean>(true)
  const contentRef = useRef<HTMLDivElement>(null)
  const expandedRef = useRef(true)

  const isMounted = useMounted()

  const toggleExpanded = () => {
    const willExpand = !expandedRef.current
    expandedRef.current = willExpand
    setExpanded(willExpand)

    // When expanding, scroll the content into view after it renders
    if (willExpand) {
      requestAnimationFrame(() => {
        contentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      })
    }
  }

  return (
    <div
      className={twMerge(
        'flex flex-col pt-1 gap-y-1 px-4 py-[5px]',
        expanded ? 'pb-4' : '',
        className,
      )}
    >
      <div className="flex w-full items-center justify-between text-primary-900 dark:text-primary-100">
        <div className="flex items-center gap-x-2 w-5/6">
          <span className="font-semibold whitespace-nowrap truncate text-sm font-medium text-primary-900 dark:text-primary-100">
            {title}
          </span>
          {isMounted && <Badge count={count} />}
        </div>
        <div className="flex items-center gap-x-1 text-primary-500 dark:text-primary-400">
          {isMounted && (
            <>
              {count !== 0 && count !== '' && (
                <IconButton
                  as="div"
                  type="button"
                  onClick={onClear}
                  className="py-0.5 px-0.5 hover:bg-primary-200"
                  tooltip="Clear"
                >
                  <HiMiniXMark className="h-4 w-4" />
                </IconButton>
              )}
              <IconButton
                as="div"
                type="button"
                onClick={toggleExpanded}
                className="py-0.5 px-0.5 hover:bg-primary-200"
                tooltip={expanded ? 'Hide' : 'Expand'}
              >
                {expanded ? (
                  <HiMiniMinus className="h-4 w-4" />
                ) : (
                  <HiMiniPlus className="h-4 w-4" />
                )}
              </IconButton>
            </>
          )}
        </div>
      </div>
      {expanded && (
        <div ref={contentRef} className="flex flex-col gap-y-3">
          {children}
        </div>
      )}
    </div>
  )
}

export default memo(FilterContainer)
